import type { Page, PlaceMatch, ZoneGeoJsonPoint, ZoneGeoJsonPolygon, ZoneInput, ZoneListQuery, ZoneMapQuery, ZoneShape, ZonesMapConfig } from '@chukua/common';
import { api } from '../../shared/shared.api';
import { toListParams } from '../../shared/policies/list-params';

export interface Zone {
  id: string;
  code: string;
  name: string;
  kind: string;
  /** ISO 3166-1 alpha-2, worked out by the API from the location; null until the zone is placed. */
  countryCode: string | null;
  isActive: boolean;
  polygon: ZoneGeoJsonPolygon | null;
  centroid: ZoneGeoJsonPoint | null;
  /** A point zone's reach; null for a polygon. */
  radiusMeters: number | null;
}

/** A row of the Zones table: no geometry, only which shape the zone has. */
export interface ZoneListItem {
  id: string;
  code: string;
  name: string;
  kind: string;
  /** Null when the zone's kind is no longer in the catalogue. */
  kindName: string | null;
  countryCode: string | null;
  isActive: boolean;
  shape: ZoneShape;
  radiusMeters: number | null;
}

export function listZones(query: ZoneListQuery): Promise<Page<ZoneListItem>> {
  return api<Page<ZoneListItem>>(`/zones?${toListParams(query)}`);
}

/** Every zone the table's search and filters match, with geometry — the map isn't limited to the table's page. */
export function listZonesForMap(query: ZoneMapQuery): Promise<Zone[]> {
  return api<Zone[]>(`/zones/map?${toListParams(query)}`);
}

export function getZone(id: string): Promise<Zone> {
  return api<Zone>(`/zones/${id}`);
}

export function createZone(input: ZoneInput): Promise<Zone> {
  return api<Zone>('/zones', { method: 'POST', body: JSON.stringify(input) });
}

export function updateZone(id: string, input: ZoneInput): Promise<Zone> {
  return api<Zone>(`/zones/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function deactivateZone(id: string): Promise<void> {
  await api<void>(`/zones/${id}/deactivate`, { method: 'POST' });
}

export async function reactivateZone(id: string): Promise<void> {
  await api<void>(`/zones/${id}/reactivate`, { method: 'POST' });
}

export function getZonesMapConfig(): Promise<ZonesMapConfig> {
  return api<ZonesMapConfig>('/zones/map-config');
}

/** Places to start a new zone from, as the admin types a name; `near` (the map's centre) ranks closer places first. */
export function searchPlaces(q: string, near?: { lng: number; lat: number }): Promise<PlaceMatch[]> {
  const params = new URLSearchParams({ q });
  if (near) {
    params.set('lng', String(near.lng));
    params.set('lat', String(near.lat));
  }
  return api<PlaceMatch[]>(`/zones/place-search?${params}`);
}
